import { useEffect, useMemo, useState } from "react";
import { getDetailFields } from "@/api/detailFields";
import type { DetailFieldsResponse, DetailFieldsSection } from "@/api/types";

export interface EffectiveDetailFieldsOptions {
  /** Per-section hardcoded fallback. Used while the fetch is in
   *  flight, when it errors, or when the backend ships a section
   *  without a key match here. The map's keys must match the
   *  section keys (e.g. "info" / "funding" / "founder" /
   *  "signals" / "financials"). */
  fallback: Record<string, DetailFieldsSection>;
}

export interface EffectiveDetailFields {
  /** Resolved-by-key sections — backend-merged where available,
   *  fallback otherwise. Indexed by section key for direct
   *  lookup (effectiveDetailFields.sections.info, etc.). */
  sections: Record<string, DetailFieldsSection>;
  /** Section keys the backend actually shipped on this project.
   *  Drives "is this section relevant for this project_type"
   *  decisions — e.g. M&A backends ship "financials" but not
   *  "funding"/"signals", and the frontend uses this set to
   *  hide the irrelevant cards. */
  backendSectionKeys: Set<string>;
  /** Backend response sections in their original order. Used by
   *  the page to build a default two-column layout (column +
   *  order ascending). Empty until the fetch resolves. */
  rawSections: DetailFieldsSection[];
  /** True until the first fetch attempt resolves (success or
   *  failure). Lets consumers gate their first-mount logic
   *  (snapshot capture, view application) on the backend
   *  catalog actually being loaded — without it, project-type-
   *  specific section keys would be treated as "unknown" and
   *  filtered out before they're recognised. */
  loading: boolean;
}

/**
 * Returns one resolved DetailFieldsSection per fallback key,
 * preferring the backend's catalog when it has a section with the
 * same key. Backend = source of truth; the hardcoded sections only
 * fill the gap while the fetch is in flight or when the catalog
 * doesn't carry that section.
 *
 * Also surfaces the raw set of section keys the backend ships on
 * this project so consumers can hide irrelevant cards (Hiring
 * signals on an M&A project, Funding on a target-acquisition
 * project, etc.).
 *
 * The hook intentionally doesn't surface loading or error state to
 * the caller — the fallback already covers both cases. A passive
 * console warning on fetch failure helps debugging without making
 * the page conditional on the catalog.
 */
export function useEffectiveDetailFields(
  slug: string,
  { fallback }: EffectiveDetailFieldsOptions,
): EffectiveDetailFields {
  const [response, setResponse] = useState<DetailFieldsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDetailFields(slug)
      .then((res) => {
        if (!cancelled) setResponse(res);
      })
      .catch((err) => {
        // Defer to the fallback silently — the operator still sees
        // a usable detail panel while we sort out the catalog.
        // eslint-disable-next-line no-console
        console.warn("getDetailFields failed, using hardcoded fallback", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return useMemo(() => {
    const byKey = new Map<string, DetailFieldsSection>();
    for (const s of response?.sections ?? []) {
      byKey.set(s.key, s);
    }
    const sections: Record<string, DetailFieldsSection> = {};
    for (const [key, fb] of Object.entries(fallback)) {
      const backendSection = byKey.get(key);
      if (!backendSection) {
        sections[key] = fb;
        continue;
      }
      // Merge fields: backend version wins for shared keys (it has
      // the authoritative metadata + ordering), but any field the
      // fallback declares that the backend didn't ship is
      // appended at the end. This way a backend catalog that
      // omits e.g. `state` from the info section doesn't strip it
      // from the customiser popover — operators still see every
      // field the frontend knows how to render.
      const backendKeys = new Set(
        backendSection.fields.map((f) => f.key),
      );
      const missing = fb.fields.filter((f) => !backendKeys.has(f.key));
      sections[key] = missing.length
        ? { ...backendSection, fields: [...backendSection.fields, ...missing] }
        : backendSection;
    }
    // Surface every backend-shipped section in `sections`, even
    // when there's no fallback for the key — consumers gating on
    // `backendSectionKeys` need access to read its fields.
    for (const s of response?.sections ?? []) {
      if (!sections[s.key]) sections[s.key] = s;
    }

    const backendSectionKeys = new Set(byKey.keys());
    const rawSections = response?.sections ?? [];

    return { sections, backendSectionKeys, rawSections, loading };
  }, [response, fallback, loading]);
}
